import { Button, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { Search } from 'lucide-react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import _, { isEmpty } from 'underscore';
import googleTagManager from 'utils/google-tag-manager';
import { getPowerBIReports } from './api/get-reports';
import PowerBIReportsTabsHeader from './components/power-bi-reports-tabs-header';
import Paper from 'components/paper';

type Props = {};

const ListReports: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const { account } = useAccountState();
  const PAGE_NAME_ID = 'powerBi.listReports';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // STATE
  const [searchValue, setSearchValue] = useState('');

  // FETCH DATA
  const { data: reports, isLoading } = useGet(
    ['getPowerBIReports', account.company?.companyId],
    (context) => getPowerBIReports(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  // HANDLERS
  const handleOnClick = (name: string, url: string) => {
    googleTagManager.push({
      event: 'reports',
      fileName: name,
      pageUrl: window.location.pathname,
    });

    history.push(url);
  };

  // RENDERS
  const renderRows = () => {
    return _.chain(reports)
      .filter((report) => (isEmpty(searchValue) ? true : report.name.toLowerCase().includes(searchValue.toLowerCase())))
      .sortBy((report) => report.name.toLowerCase())
      .value();
  };

  return (
    <>
      <PowerBIReportsTabsHeader utils={<Breadcrumbs />} />
      <Paper transparent>
        <FilterSidebar>
          <TextField
            label={intl.formatMessage({ id: 'general.search' })}
            value={searchValue}
            endAdornment={<Search size={18} />}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </FilterSidebar>
        <List
          options={[{ id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) }]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {renderRows().map((report) => (
            <ListRow key={report.id} onClick={() => handleOnClick(report.name, `/powerbi/reports/${report.id}`)}>
              <ListCell
                sx={{
                  padding: 4,
                }}
                align="left">
                <span style={{ whiteSpace: 'break-spaces' }}>{report.name}</span>
              </ListCell>
              <ListCell align="right">
                <Button
                  fullWidth={isMobile}
                  size={'small'}
                  onClick={() => handleOnClick(report.name, `/powerbi/reports/${report.id}`)}>
                  <FormattedMessage id="general.view" />
                </Button>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </>
  );
};

export default ListReports;
